import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box, Flex, Text, Switch, Input, Button, VStack, HStack, Spinner, Center,
  useToast, Badge, InputGroup, InputLeftAddon, Divider, Icon, Code,
  Textarea, Avatar, FormControl, FormLabel, Select, Collapse, UnorderedList, ListItem,
} from "@chakra-ui/react";
import { Save, Wifi, WifiOff, Webhook, Camera, UserCircle, ListChecks, BookOpen } from "lucide-react";
import {
  getCrmConfig, salvarCrmConfig, getCrmZapiStatus, configurarCrmWebhooks, atualizarCrmPerfil,
  getCrmTarefasConfig, salvarCrmTarefasConfig,
} from "../../services/api";
import { PRIORIDADES, formatDate } from "./crmUtils";

const ACCENT = "#F68633";

// SLA é guardado em horas; a tela mostra em dias quando o valor é redondo.
const emDias = (h) => Number(h) >= 24 && Number(h) % 24 === 0;
const paraExibicao = (h) => (emDias(h) ? { valor: Number(h) / 24, unidade: "dias" } : { valor: Number(h) || 1, unidade: "horas" });
const paraHoras = (valor, unidade) => Math.max(1, Math.round(Number(valor) || 1) * (unidade === "dias" ? 24 : 1));

/** Campo numérico curto com sufixo, no mesmo padrão dos outros blocos da aba. */
function Campo({ label, valor, onChange, sufixo, largura = "90px", prefixo }) {
  return (
    <Flex justify="space-between" align="center" gap={2}>
      <Text fontSize="sm" color="gray.600">{label}</Text>
      <HStack spacing={1} flexShrink={0}>
        {prefixo && <Text fontSize="sm" color="gray.500">{prefixo}</Text>}
        <Input type="number" size="sm" w={largura} value={valor ?? ""} onChange={(e) => onChange(e.target.value)} />
        {sufixo && <Text fontSize="sm" color="gray.500">{sufixo}</Text>}
      </HStack>
    </Flex>
  );
}

/** Manual da tarefa (V1.7.1): o "como usar" do catálogo, aqui só para leitura. */
function ComoUsar({ tarefa }) {
  const cuidados = Array.isArray(tarefa.cuidados) ? tarefa.cuidados : [];
  const linhas = [
    ["Objetivo", tarefa.objetivo],
    ["Quem entra", tarefa.elegibilidade],
    ["Contexto entregue", tarefa.contexto],
    ["Canal", tarefa.canal],
    ["Abordagem", tarefa.abordagem],
    ["Cumprido quando", tarefa.cumprido],
  ].filter(([, v]) => !!v);

  if (!linhas.length && !cuidados.length) return null;

  return (
    <Box bg="purple.50" borderRadius="md" px={3} py={2.5} mb={3}>
      <VStack align="stretch" spacing={1}>
        {linhas.map(([rotulo, valor]) => (
          <Text key={rotulo} fontSize="xs" color="gray.700"><b>{rotulo}:</b> {valor}</Text>
        ))}
        {cuidados.length > 0 && (
          <Box>
            <Text fontSize="xs" color="gray.700"><b>Cuidados:</b></Text>
            <UnorderedList spacing={0.5} pl={4}>
              {cuidados.map((c, i) => <ListItem key={i}><Text fontSize="xs" color="gray.700">{c}</Text></ListItem>)}
            </UnorderedList>
          </Box>
        )}
        {tarefa.exemplo && (
          <Text fontSize="xs" color="gray.500" fontStyle="italic" mt={1}>Exemplo: {tarefa.exemplo}</Text>
        )}
      </VStack>
    </Box>
  );
}

/**
 * Uma das 7 tarefas automáticas (V1.7): liga/desliga, SLA, prioridade, a janela
 * de recorrência e os parâmetros do próprio gatilho. O botão "Como usar" abre o
 * manual da tarefa — o mesmo que o vendedor lê no card do Kanban.
 */
function TarefaAutomatica({ tarefa, onChange }) {
  const sla = paraExibicao(tarefa.sla_horas);
  const p = tarefa.params || {};
  const setParam = (k, v) => onChange({ params: { ...p, [k]: v } });
  const [verComoUsar, setVerComoUsar] = useState(false);

  return (
    <Box borderWidth="1px" borderColor="gray.100" borderRadius="lg" p={4} bg={tarefa.ativo ? "white" : "gray.50"}>
      <Flex justify="space-between" align="start" gap={3} mb={tarefa.ativo || verComoUsar ? 3 : 0}>
        <Box minW={0}>
          <HStack spacing={2}>
            <Badge colorScheme="purple" variant="subtle">{tarefa.codigo}</Badge>
            <Text fontWeight="semibold" fontSize="sm">{tarefa.nome}</Text>
          </HStack>
          <Text fontSize="xs" color="gray.500" mt={1}>{tarefa.gatilho}</Text>
          <Button
            size="xs" variant="link" colorScheme="purple" mt={1}
            leftIcon={<BookOpen size={12} />}
            onClick={() => setVerComoUsar((v) => !v)}
          >
            {verComoUsar ? "Ocultar como usar" : "Como usar"}
          </Button>
        </Box>
        <Switch
          colorScheme="orange"
          isChecked={!!tarefa.ativo}
          onChange={(e) => onChange({ ativo: e.target.checked })}
        />
      </Flex>

      <Collapse in={verComoUsar} animateOpacity>
        <ComoUsar tarefa={tarefa} />
      </Collapse>

      {tarefa.ativo && (
        <VStack align="stretch" spacing={3}>
          <Flex justify="space-between" align="center" gap={2}>
            <Text fontSize="sm" color="gray.600">SLA (prazo a partir do gatilho)</Text>
            <HStack spacing={1} flexShrink={0}>
              <Input
                type="number" size="sm" w="80px" value={sla.valor}
                onChange={(e) => onChange({ sla_horas: paraHoras(e.target.value, sla.unidade) })}
              />
              <Select
                size="sm" w="90px" value={sla.unidade}
                onChange={(e) => onChange({ sla_horas: paraHoras(sla.valor, e.target.value) })}
              >
                <option value="horas">horas</option>
                <option value="dias">dias</option>
              </Select>
            </HStack>
          </Flex>

          <Flex justify="space-between" align="center" gap={2}>
            <Text fontSize="sm" color="gray.600">Prioridade padrão</Text>
            <Select
              size="sm" w="110px" flexShrink={0} value={tarefa.prioridade}
              onChange={(e) => onChange({ prioridade: Number(e.target.value) })}
            >
              {PRIORIDADES.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
            </Select>
          </Flex>

          {/* Parâmetros próprios do gatilho */}
          {tarefa.tipo === "toque_periodico" && (
            <Campo label="Gerar após" sufixo="dias sem atividade" valor={p.dias_sem_atividade} onChange={(v) => setParam("dias_sem_atividade", v)} />
          )}
          {tarefa.tipo === "resgate" && (
            <Campo label="Gerar após" sufixo="dias sem comprar" valor={p.dias_sem_comprar} onChange={(v) => setParam("dias_sem_comprar", v)} />
          )}
          {tarefa.tipo === "reativar_alto_valor" && (
            <>
              <Campo label="Queda de valor no período de pelo menos" sufixo="%" valor={p.queda_pct} onChange={(v) => setParam("queda_pct", v)} />
              <Campo label="Só contas que compravam acima de" prefixo="R$" largura="120px" valor={p.valor_min} onChange={(v) => setParam("valor_min", v)} />
            </>
          )}
          {tarefa.tipo === "pos_venda" && (
            <Campo label="Gerar" sufixo="dias após a entrega" valor={p.dias_apos_entrega} onChange={(v) => setParam("dias_apos_entrega", v)} />
          )}
          {tarefa.tipo === "abertura" && (
            <>
              <Flex justify="space-between" align="center" gap={2}>
                <Text fontSize="sm" color="gray.600">Só contas CNPJ (sem a cauda longa de pessoa física)</Text>
                <Switch
                  size="sm" colorScheme="orange" flexShrink={0}
                  isChecked={Number(p.apenas_cnpj) === 1}
                  onChange={(e) => setParam("apenas_cnpj", e.target.checked ? 1 : 0)}
                />
              </Flex>
              <Box>
                <Text fontSize="sm" color="gray.600" mb={1}>Segmentos considerados de potencial</Text>
                <Input
                  size="sm" value={p.segmentos ?? ""}
                  placeholder="transportadora,locadora,frota_grande,..."
                  onChange={(e) => setParam("segmentos", e.target.value)}
                />
                <Text fontSize="xs" color="gray.400" mt={1}>
                  Separados por vírgula. Conta ainda sem segmento também entra — confirmar o segmento é um dos objetivos da abertura.
                </Text>
              </Box>
            </>
          )}

          <Campo
            label={tarefa.recorrencia_dias === 0 ? "Repetir na mesma conta (0 = nunca repete)" : "Repetir na mesma conta só após"}
            sufixo="dias" valor={tarefa.recorrencia_dias}
            onChange={(v) => onChange({ recorrencia_dias: Number(v) || 0 })}
          />
        </VStack>
      )}
    </Box>
  );
}

function Regra({ ativo, onToggle, titulo, children }) {
  return (
    <Box borderWidth="1px" borderColor="gray.100" borderRadius="lg" p={4} bg={ativo ? "white" : "gray.50"}>
      <Flex justify="space-between" align="center" mb={ativo ? 3 : 0}>
        <Text fontWeight="semibold" fontSize="sm">{titulo}</Text>
        <Switch colorScheme="orange" isChecked={ativo} onChange={onToggle} />
      </Flex>
      {ativo && <Box>{children}</Box>}
    </Box>
  );
}

export default function ConfiguracoesTab() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [zapi, setZapi] = useState(null);
  const [reconf, setReconf] = useState(false);
  const [perfil, setPerfil] = useState({ nome: "", descricao: "" });
  const [foto, setFoto] = useState(null);
  const [fotoPreview, setFotoPreview] = useState(null);
  const [salvandoPerfil, setSalvandoPerfil] = useState(false);
  const [tarefas, setTarefas] = useState([]);
  const [globais, setGlobais] = useState(null);
  const fileRef = useRef(null);
  const toast = useToast();

  const carregar = useCallback(async () => {
    setLoading(true);
    const [c, z, t] = await Promise.all([getCrmConfig(), getCrmZapiStatus(), getCrmTarefasConfig()]);
    if (c?.data) setCfg(c.data);
    if (z) setZapi(z);
    if (t?.tarefas) { setTarefas(t.tarefas); setGlobais(t.globais); }
    setLoading(false);
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const set = (k, v) => setCfg((p) => ({ ...p, [k]: v }));

  const salvar = async () => {
    setSalvando(true);
    const payload = {
      op_cnpj_ativo: !!cfg.op_cnpj_ativo,
      op_cnpj_total_min: Number(cfg.op_cnpj_total_min) || 0,
      op_recorrente_ativo: !!cfg.op_recorrente_ativo,
      op_recorrente_min_pedidos: Number(cfg.op_recorrente_min_pedidos) || 1,
      op_recompra_ativo: !!cfg.op_recompra_ativo,
      op_recompra_dias: Number(cfg.op_recompra_dias) || 1,
      op_ticket_ativo: !!cfg.op_ticket_ativo,
      op_ticket_medio_min: Number(cfg.op_ticket_medio_min) || 0,
      op_ml_ativo: !!cfg.op_ml_ativo,
      op_ml_volume_ativo: !!cfg.op_ml_volume_ativo,
      op_ml_total_min: Number(cfg.op_ml_total_min) || 0,
      op_ml_pedidos_ativo: !!cfg.op_ml_pedidos_ativo,
      op_ml_min_pedidos: Number(cfg.op_ml_min_pedidos) || 1,
      rfm_periodo_dias: Number(cfg.rfm_periodo_dias) || 180,
      saude_verde_dias: Number(cfg.saude_verde_dias) || 60,
      saude_amarelo_dias: Number(cfg.saude_amarelo_dias) || 120,
    };

    // As tarefas automáticas têm endpoint próprio (crm_tarefas_config); o botão
    // "Salvar configurações" grava os dois de uma vez.
    const [r, rt] = await Promise.all([
      salvarCrmConfig(payload),
      salvarCrmTarefasConfig({
        tarefas: tarefas.map((t) => ({
          tipo: t.tipo,
          ativo: !!t.ativo,
          sla_horas: Number(t.sla_horas) || 48,
          prioridade: Number(t.prioridade) || 0,
          recorrencia_dias: Number(t.recorrencia_dias) || 0,
          params: t.params || {},
        })),
        globais: {
          tarefa_max_tentativas: Number(globais?.tarefa_max_tentativas) || 3,
          tarefa_intervalos_dias: String(globais?.tarefa_intervalos_dias || "1,3,7"),
          tarefa_cooldown_dias: Number(globais?.tarefa_cooldown_dias) || 90,
          tarefa_teto_diario_vendedor: Number(globais?.tarefa_teto_diario_vendedor) || 0,
        },
      }),
    ]);
    setSalvando(false);

    if (r?.data && rt?.ok) {
      if (rt.tarefas) setTarefas(rt.tarefas);
      toast({ status: "success", title: "Configurações salvas" });
    } else {
      toast({ status: "error", title: "Erro ao salvar", description: rt?.error || r?.error });
    }
  };

  const setTarefa = (tipo, patch) =>
    setTarefas((lista) => lista.map((t) => (t.tipo === tipo ? { ...t, ...patch } : t)));
  const setGlobal = (k, v) => setGlobais((g) => ({ ...g, [k]: v }));

  const reconfigurar = async () => {
    setReconf(true);
    const r = await configurarCrmWebhooks();
    setReconf(false);
    if (r?.data) { toast({ status: "success", title: "Webhooks reconfigurados" }); carregar(); }
    else toast({ status: "error", title: "Erro", description: r?.error });
  };

  const onFoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFoto(f);
    setFotoPreview(URL.createObjectURL(f));
  };

  const salvarPerfil = async () => {
    const fd = new FormData();
    let algo = false;
    if (perfil.nome.trim()) { fd.append("nome", perfil.nome.trim()); algo = true; }
    if (perfil.descricao.trim()) { fd.append("descricao", perfil.descricao.trim()); algo = true; }
    if (foto) { fd.append("foto", foto); algo = true; }
    if (!algo) { toast({ status: "info", title: "Nada para atualizar" }); return; }
    setSalvandoPerfil(true);
    const r = await atualizarCrmPerfil(fd);
    setSalvandoPerfil(false);
    if (r?.data) {
      const falhas = Object.entries(r.data).filter(([, v]) => !v.ok).map(([k]) => k);
      if (falhas.length) toast({ status: "warning", title: "Atualizado parcialmente", description: `Falhou: ${falhas.join(", ")}` });
      else toast({ status: "success", title: "Perfil atualizado" });
      setFoto(null);
    } else {
      toast({ status: "error", title: "Erro ao atualizar perfil", description: r?.error });
    }
  };

  if (loading || !cfg) return <Center py={16}><Spinner color={ACCENT} /></Center>;

  const conectado = zapi?.status?.connected;

  return (
    <VStack align="stretch" spacing={6} maxW="760px">
      {/* Z-API */}
      <Box borderWidth="1px" borderColor="gray.100" borderRadius="lg" p={4}>
        <Flex justify="space-between" align="center" mb={3}>
          <HStack>
            <Icon as={conectado ? Wifi : WifiOff} color={conectado ? "green.500" : "red.500"} />
            <Text fontWeight="bold" fontSize="sm">Instância Z-API (relacionamento TAGA)</Text>
          </HStack>
          <Badge colorScheme={conectado ? "green" : "red"}>{conectado ? "Conectado" : "Desconectado"}</Badge>
        </Flex>
        <Text fontSize="xs" color="gray.500" mb={1}>Webhook configurado para receber mensagens:</Text>
        <Code fontSize="11px" p={2} borderRadius="md" w="100%" whiteSpace="normal" wordBreak="break-all">
          {zapi?.webhook_url || "—"}
        </Code>
        <Button mt={3} size="sm" variant="outline" leftIcon={<Webhook size={15} />} onClick={reconfigurar} isLoading={reconf}>
          Reconfigurar webhooks
        </Button>
      </Box>

      {/* Perfil do WhatsApp */}
      <Box borderWidth="1px" borderColor="gray.100" borderRadius="lg" p={4}>
        <HStack mb={1}>
          <Icon as={UserCircle} color={ACCENT} />
          <Text fontWeight="bold" fontSize="sm">Perfil do WhatsApp</Text>
        </HStack>
        <Text fontSize="xs" color="gray.500" mb={3}>
          Foto, nome e recado exibidos no WhatsApp do CRM. Deixe em branco o que não quiser alterar.
        </Text>
        <VStack align="stretch" spacing={3}>
          <HStack spacing={3} align="center">
            <Avatar size="lg" src={fotoPreview || undefined} icon={<Camera size={22} />} bg="gray.200" />
            <VStack align="start" spacing={1}>
              <Button size="sm" variant="outline" leftIcon={<Camera size={15} />} onClick={() => fileRef.current?.click()}>Escolher foto</Button>
              {foto && <Text fontSize="xs" color="gray.500" noOfLines={1} maxW="220px">{foto.name}</Text>}
            </VStack>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={onFoto} />
          </HStack>
          <FormControl>
            <FormLabel fontSize="xs" mb={1}>Nome do perfil</FormLabel>
            <Input size="sm" maxLength={80} placeholder="Nome exibido no WhatsApp" value={perfil.nome} onChange={(e) => setPerfil((p) => ({ ...p, nome: e.target.value }))} />
          </FormControl>
          <FormControl>
            <FormLabel fontSize="xs" mb={1}>Recado / descrição</FormLabel>
            <Textarea size="sm" rows={2} maxLength={300} placeholder="Ex.: Autopeças TAGA — atendimento e vendas" value={perfil.descricao} onChange={(e) => setPerfil((p) => ({ ...p, descricao: e.target.value }))} />
          </FormControl>
          <Button size="sm" alignSelf="start" bg={ACCENT} color="white" _hover={{ bg: "#d4702a" }} leftIcon={<Save size={15} />} onClick={salvarPerfil} isLoading={salvandoPerfil} isDisabled={!conectado}>
            Salvar perfil
          </Button>
          {!conectado && <Text fontSize="xs" color="red.400">Conecte a instância Z-API para editar o perfil.</Text>}
        </VStack>
      </Box>

      <Divider />

      {/* Regras */}
      <Box>
        <Text fontWeight="bold" fontSize="sm" mb={1}>Regras de oportunidades</Text>
        <Text fontSize="xs" color="gray.500" mb={4}>Defina quais clientes aparecem na aba Oportunidades.</Text>

        <VStack align="stretch" spacing={3}>
          <Regra titulo="CNPJ com alto volume de compras" ativo={!!cfg.op_cnpj_ativo} onToggle={(e) => set("op_cnpj_ativo", e.target.checked)}>
            <HStack>
              <Text fontSize="sm" color="gray.600">Total de compras acima de</Text>
              <InputGroup size="sm" w="180px">
                <InputLeftAddon>R$</InputLeftAddon>
                <Input type="number" value={cfg.op_cnpj_total_min} onChange={(e) => set("op_cnpj_total_min", e.target.value)} />
              </InputGroup>
            </HStack>
          </Regra>

          <Regra titulo="Clientes recorrentes" ativo={!!cfg.op_recorrente_ativo} onToggle={(e) => set("op_recorrente_ativo", e.target.checked)}>
            <HStack>
              <Text fontSize="sm" color="gray.600">A partir de</Text>
              <Input type="number" size="sm" w="90px" value={cfg.op_recorrente_min_pedidos} onChange={(e) => set("op_recorrente_min_pedidos", e.target.value)} />
              <Text fontSize="sm" color="gray.600">pedidos</Text>
            </HStack>
          </Regra>

          <Regra titulo="Recompra (clientes que sumiram)" ativo={!!cfg.op_recompra_ativo} onToggle={(e) => set("op_recompra_ativo", e.target.checked)}>
            <HStack>
              <Text fontSize="sm" color="gray.600">Sem comprar há mais de</Text>
              <Input type="number" size="sm" w="90px" value={cfg.op_recompra_dias} onChange={(e) => set("op_recompra_dias", e.target.value)} />
              <Text fontSize="sm" color="gray.600">dias</Text>
            </HStack>
          </Regra>

          <Regra titulo="Ticket médio alto" ativo={!!cfg.op_ticket_ativo} onToggle={(e) => set("op_ticket_ativo", e.target.checked)}>
            <HStack>
              <Text fontSize="sm" color="gray.600">Ticket médio acima de</Text>
              <InputGroup size="sm" w="180px">
                <InputLeftAddon>R$</InputLeftAddon>
                <Input type="number" value={cfg.op_ticket_medio_min} onChange={(e) => set("op_ticket_medio_min", e.target.value)} />
              </InputGroup>
            </HStack>
          </Regra>

          <Regra titulo="Exclusivos do Mercado Livre (apenas CNPJ)" ativo={!!cfg.op_ml_ativo} onToggle={(e) => set("op_ml_ativo", e.target.checked)}>
            <Text fontSize="xs" color="gray.500" mb={3}>
              Clientes que só compraram pelo Mercado Livre — nunca direto, e-commerce ou outros canais. Selecione um ou os dois critérios (ambos = exige os dois).
            </Text>
            <VStack align="stretch" spacing={3}>
              <Flex justify="space-between" align="center" gap={2}>
                <HStack>
                  <Switch size="sm" colorScheme="orange" isChecked={!!cfg.op_ml_volume_ativo} onChange={(e) => set("op_ml_volume_ativo", e.target.checked)} />
                  <Text fontSize="sm" color="gray.600">Volume total acima de</Text>
                </HStack>
                <InputGroup size="sm" w="170px">
                  <InputLeftAddon>R$</InputLeftAddon>
                  <Input type="number" value={cfg.op_ml_total_min} onChange={(e) => set("op_ml_total_min", e.target.value)} isDisabled={!cfg.op_ml_volume_ativo} />
                </InputGroup>
              </Flex>
              <Flex justify="space-between" align="center" gap={2}>
                <HStack>
                  <Switch size="sm" colorScheme="orange" isChecked={!!cfg.op_ml_pedidos_ativo} onChange={(e) => set("op_ml_pedidos_ativo", e.target.checked)} />
                  <Text fontSize="sm" color="gray.600">Nº de pedidos a partir de</Text>
                </HStack>
                <Input type="number" size="sm" w="90px" value={cfg.op_ml_min_pedidos} onChange={(e) => set("op_ml_min_pedidos", e.target.value)} isDisabled={!cfg.op_ml_pedidos_ativo} />
              </Flex>
            </VStack>
          </Regra>
        </VStack>

        <Divider my={5} />
        <Text fontWeight="bold" fontSize="sm" mb={1}>Saúde da carteira</Text>
        <Text fontSize="xs" color="gray.500" mb={4}>Como a recência classifica o risco de cada conta.</Text>
        <VStack align="stretch" spacing={3}>
          <HStack justify="space-between">
            <Text fontSize="sm" color="gray.600">Período de análise (frequência e valor)</Text>
            <HStack>
              <Input type="number" size="sm" w="90px" value={cfg.rfm_periodo_dias ?? ""} onChange={(e) => set("rfm_periodo_dias", e.target.value)} />
              <Text fontSize="sm" color="gray.500">dias</Text>
            </HStack>
          </HStack>
          <HStack justify="space-between">
            <Text fontSize="sm" color="gray.600">
              <Box as="span" w="9px" h="9px" bg="#22c55e" borderRadius="full" display="inline-block" mr={2} />Saudável até
            </Text>
            <HStack>
              <Input type="number" size="sm" w="90px" value={cfg.saude_verde_dias ?? ""} onChange={(e) => set("saude_verde_dias", e.target.value)} />
              <Text fontSize="sm" color="gray.500">dias</Text>
            </HStack>
          </HStack>
          <HStack justify="space-between">
            <Text fontSize="sm" color="gray.600">
              <Box as="span" w="9px" h="9px" bg="#eab308" borderRadius="full" display="inline-block" mr={2} />Atenção até (acima = em risco)
            </Text>
            <HStack>
              <Input type="number" size="sm" w="90px" value={cfg.saude_amarelo_dias ?? ""} onChange={(e) => set("saude_amarelo_dias", e.target.value)} />
              <Text fontSize="sm" color="gray.500">dias</Text>
            </HStack>
          </HStack>
        </VStack>

        {/* Tarefas automáticas (V1.7) */}
        <Divider my={5} />
        <HStack mb={1}>
          <Icon as={ListChecks} color={ACCENT} boxSize={4} />
          <Text fontWeight="bold" fontSize="sm">Tarefas automáticas</Text>
        </HStack>
        <Text fontSize="xs" color="gray.500" mb={4}>
          As tarefas do fluxo são geradas sozinhas, de hora em hora, para as contas da carteira.
          Aqui você liga, desliga e ajusta o prazo, a prioridade e o gatilho de cada uma.
          O limiar de reativação (4.1) mora agora dentro da própria tarefa.
        </Text>

        <VStack align="stretch" spacing={3}>
          {tarefas.map((t) => (
            <TarefaAutomatica key={t.tipo} tarefa={t} onChange={(patch) => setTarefa(t.tipo, patch)} />
          ))}
        </VStack>

        {globais && (
          <Box mt={4} borderWidth="1px" borderColor="gray.100" borderRadius="lg" p={4}>
            <Text fontWeight="semibold" fontSize="sm" mb={1}>Regras que valem para todas</Text>
            <Text fontSize="xs" color="gray.500" mb={3}>
              Política de tentativas quando o cliente não responde e o descanso da conta após um desfecho "cliente perdido".
            </Text>
            <VStack align="stretch" spacing={3}>
              <Campo
                label="Máximo de tentativas de contato" sufixo="tentativas"
                valor={globais.tarefa_max_tentativas} onChange={(v) => setGlobal("tarefa_max_tentativas", v)}
              />
              <Flex justify="space-between" align="center" gap={2}>
                <Text fontSize="sm" color="gray.600">Intervalos entre as tentativas</Text>
                <HStack spacing={1} flexShrink={0}>
                  <Input
                    size="sm" w="110px" value={globais.tarefa_intervalos_dias ?? ""}
                    placeholder="1,3,7"
                    onChange={(e) => setGlobal("tarefa_intervalos_dias", e.target.value)}
                  />
                  <Text fontSize="sm" color="gray.500">dias</Text>
                </HStack>
              </Flex>
              <Campo
                label="Cooldown após um desfecho PERDIDO" sufixo="dias"
                valor={globais.tarefa_cooldown_dias} onChange={(v) => setGlobal("tarefa_cooldown_dias", v)}
              />
              <Campo
                label="Teto de tarefas automáticas por vendedor (0 = sem teto)" sufixo="por dia" largura="90px"
                valor={globais.tarefa_teto_diario_vendedor} onChange={(v) => setGlobal("tarefa_teto_diario_vendedor", v)}
              />
              {globais.tarefas_auto_desde && (
                <Text fontSize="xs" color="gray.400">
                  Gatilhos de evento (abertura e pós-venda) valem a partir de{" "}
                  {formatDate(globais.tarefas_auto_desde)} — nada retroativo.
                </Text>
              )}
            </VStack>
          </Box>
        )}

        <Button mt={5} bg={ACCENT} color="white" _hover={{ bg: "#d4702a" }} leftIcon={<Save size={16} />} onClick={salvar} isLoading={salvando}>
          Salvar configurações
        </Button>
      </Box>
    </VStack>
  );
}
